import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import './Courses.css';

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // Options: 'all', 'basic', 'advanced'
  const [clock, setClock] = useState(new Date());

  const SERVER_URL = 'https://api.avtechbmt.cloud';
  const API_BASE_URL = `${SERVER_URL}/api/v1`;

  /* Live clock for editorial rail */
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/courses`);
        if (!response.ok) throw new Error('Failed to fetch courses');
        const data = await response.json();
        setCourses(data.data || data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, [API_BASE_URL]);

  const getImageUrl = (path) => {
    if (!path) return 'https://via.placeholder.com/800x1000?text=No+Image';
    const cleanPath = path.replace(/\\/g, '/');
    return `${SERVER_URL}/${cleanPath}`;
  };

  /* Filter courses based on basic or advanced level */
  const filteredCourses = useMemo(() => {
    if (filter === 'all') return courses;

    return courses.filter((course) => {
      const levelText = `${course.level || ''} ${course.title || ''} ${course.category || ''}`.toLowerCase();

      if (filter === 'basic') {
        return levelText.includes('basic') || levelText.includes('beginner') || levelText.includes('foundation');
      }

      if (filter === 'advanced') {
        return levelText.includes('advanc') || levelText.includes('expert') || levelText.includes('master');
      }

      return true;
    });
  }, [courses, filter]);

  /* ---------- Loading ---------- */
  if (loading) {
    return (
      <div className="courses-page">
        <div className="courses-page__grain" aria-hidden="true" />
        <div className="courses-page__loader">
          <div className="courses-page__loader-tag">// Loading programs</div>
          <div className="courses-page__loader-title">Fetching curriculum…</div>
          <div className="courses-page__loader-bar">
            <div className="courses-page__loader-bar-fill" />
          </div>
        </div>
      </div>
    );
  }

  /* ---------- Error ---------- */
  if (error) {
    return (
      <div className="courses-page">
        <div className="courses-page__grain" aria-hidden="true" />
        <div className="courses-page__error">
          <div className="courses-page__error-tag">// Error 0x1</div>
          <h2 className="courses-page__error-title">
            Something went <em>wrong.</em>
          </h2>
          <p className="courses-page__error-message">{error}</p>
          <button
            className="courses-page__btn"
            onClick={() => window.location.reload()}
          >
            <span>Try again</span>
            <svg className="courses-page__btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="courses-page">
      {/* Paper grain */}
      <div className="courses-page__grain" aria-hidden="true" />

      {/* Editorial side rails */}
      <div className="courses-page__rail courses-page__rail--left">
        <span className="courses-page__rail-text">AVTECH · ACADEMY</span>
        <span className="courses-page__rail-line" />
        <span className="courses-page__rail-text">
          {clock.toLocaleTimeString('en-US', { hour12: false })}
        </span>
      </div>
      <div className="courses-page__rail courses-page__rail--right">
        <span className="courses-page__rail-text">COHORT · 2026</span>
        <span className="courses-page__rail-line" />
        <span className="courses-page__rail-text">↓</span>
      </div>

      {/* Giant background type */}
      <div className="courses-page__giant-type" aria-hidden="true">
        <span>PROGRAMS</span>
      </div>

      {/* ==============================
          HERO
         ============================== */}
      <section className="courses-page__hero">
        <div className="courses-page__hero-inner">
          <div className="courses-page__hero-badge">
            <span className="courses-page__badge-dot" />
            <span className="courses-page__tag">04 / Learning Programs</span>
          </div>

          <h1 className="courses-page__hero-title">
            Engineer<br />
            the <em>future.</em>
          </h1>

          <p className="courses-page__hero-sub">
            Production-level training designed to transform developers into architects —
            cohort-based, mentor-led, and relentlessly practical.
          </p>

          {/* Editorial stat row */}
          <div className="courses-page__hero-stats">
            <div className="courses-page__hero-stat">
              <span className="courses-page__hero-stat-value">{courses.length}</span>
              <span className="courses-page__hero-stat-label">Active programs</span>
            </div>
            <div className="courses-page__hero-stat">
              <span className="courses-page__hero-stat-value">100+</span>
              <span className="courses-page__hero-stat-label">Graduates</span>
            </div>
            <div className="courses-page__hero-stat">
              <span className="courses-page__hero-stat-value">92%</span>
              <span className="courses-page__hero-stat-label">Success rate</span>
            </div>
            <div className="courses-page__hero-stat">
              <span className="courses-page__hero-stat-value">1:1</span>
              <span className="courses-page__hero-stat-label">Mentor reviews</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==============================
          COURSES GRID
         ============================== */}
      <section className="courses-page__grid-section">
        <div className="courses-page__container">
          <div className="courses-page__header-wrap">
            <div className="courses-page__section-header">
              <span className="courses-page__tag">// Curriculum</span>
              <h2 className="courses-page__section-title">
                Choose your <em>path.</em>
              </h2>
              <p className="courses-page__section-sub">
                Every program is scoped like a design project — clear outcomes,
                senior mentors, and a portfolio you can defend in interviews.
              </p>
            </div>

            {/* Level Filters */}
            <div className="courses-page__filter-bar">
              <button
                className={`courses-page__filter-btn ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All Courses ({courses.length})
              </button>
              <button
                className={`courses-page__filter-btn ${filter === 'basic' ? 'active' : ''}`}
                onClick={() => setFilter('basic')}
              >
                Basic
              </button>
              <button
                className={`courses-page__filter-btn ${filter === 'advanced' ? 'active' : ''}`}
                onClick={() => setFilter('advanced')}
              >
                Advanced
              </button>
            </div>
          </div>

          {filteredCourses.length === 0 ? (
            <div className="courses-page__empty-state">
              <span className="courses-page__tag">// No Match</span>
              <p>No courses found matching the selected level.</p>
              <button
                className="courses-page__filter-btn active"
                onClick={() => setFilter('all')}
              >
                Show All Courses
              </button>
            </div>
          ) : (
            <div className="courses-page__grid">
              {filteredCourses.map((course, idx) => (
                <article className="courses-page__card" key={course._id || idx}>
                  {/* Visual */}
                  <div className="courses-page__card-visual">
                    <div className="courses-page__card-image-wrapper">
                      <img
                        src={getImageUrl(course.imageUrl)}
                        alt={course.title}
                        className="courses-page__card-image"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src =
                            'https://via.placeholder.com/800x1000?text=Course+Image';
                        }}
                      />
                    </div>

                    {course.category && (
                      <span className="courses-page__card-category">{course.category}</span>
                    )}
                    <span className="courses-page__card-index">0{idx + 1}</span>
                  </div>

                  {/* Content */}
                  <div className="courses-page__card-content">
                    <div className="courses-page__card-head">
                      <span className="courses-page__card-level">
                        {course.level || course.code || 'Core'}
                      </span>
                      {course.duration && (
                        <span className="courses-page__card-duration">{course.duration}</span>
                      )}
                    </div>

                    <h3 className="courses-page__card-title">{course.title}</h3>

                    <p className="courses-page__card-description">
                      {course.detailedDescription || course.description}
                    </p>

                    <div className="courses-page__card-meta">
                      {course.projects > 0 && (
                        <div className="courses-page__card-meta-item">
                          <span className="courses-page__card-meta-num">
                            {String(course.projects).padStart(2, '0')}
                          </span>
                          <span className="courses-page__card-meta-label">Projects</span>
                        </div>
                      )}
                      {course.internship && (
                        <div className="courses-page__card-meta-item">
                          <span className="courses-page__card-meta-num">01</span>
                          <span className="courses-page__card-meta-label">Internship</span>
                        </div>
                      )}
                    </div>

                    <Link
                      to={`/courses/${course._id}`}
                      className="courses-page__card-cta"
                    >
                      <span>View details</span>
                      <svg
                        className="courses-page__link-arrow"
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M3 8h10M9 4l4 4-4 4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ==============================
          SANSKRIT CTA
         ============================== */}
      <section className="courses-page__cta-section">
        <div className="courses-page__giant-type courses-page__giant-type--dark" aria-hidden="true">
          <span>साधना</span>
        </div>

        <div className="courses-page__cta-inner">
          <span className="courses-page__cta-tag">06 / The Call to Mastery</span>

          <h2 className="courses-page__cta-shloka">“योगः कर्मसु कौशलम्”</h2>

          <div className="courses-page__cta-divider" />

          <p className="courses-page__cta-translation">
            <strong>Yogaḥ karmasu kauśalam.</strong>
          </p>

          <p className="courses-page__cta-explanation">
            "Yoga is excellence in action." True mastery is not just knowing —
            it is executing with supreme precision, focus, and elegance.
          </p>

          <a href="#apply" className="courses-page__btn courses-page__btn--large">
            <span>Begin your Sadhana</span>
            <svg className="courses-page__btn-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M3 8h10M9 4l4 4-4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </div>
      </section>
    </div>
  );
}